import { Button } from 'designSystem/components/Button'
import { Field, FieldTitle } from 'designSystem/components/Field'
import { Input } from 'designSystem/components/Input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { Info } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoMeasurement,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
} from 'types'

const FieldBlockField = styled(Field)`
  min-width: 0;
  gap: 0.5rem;
`

const ParameterField = styled(Field)`
  gap: 0.375rem;
`

const ParameterHeaderRow = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.375rem;
`

const TruncatedFieldTitle = styled(FieldTitle)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HelpButton = styled(Button)`
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  color: var(--muted-foreground);

  &:hover {
    color: var(--foreground);
  }
`

const HelpIcon = styled(Info)`
  width: 0.75rem;
  height: 0.75rem;
`

const HelpTooltipContent = styled(TooltipContent)`
  max-width: 18rem;
  border: 1px solid var(--border);
  border-radius: calc(var(--radius) - 2px);
  background-color: var(--popover);
  padding: 0.75rem;
  color: var(--popover-foreground);
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
`

const HelpTitle = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 600;
`

const HelpText = styled.p`
  margin-top: 0.25rem;
  font-size: 0.75rem;
  line-height: 1.25rem;
  color: var(--muted-foreground);
`

const ShiftMetricLabel = styled.div`
  font-size: 0.625rem;
  text-transform: uppercase;
  color: var(--muted-foreground);
`

const ShiftMetricInput = styled(Input)`
  margin-top: 0.25rem;
  height: 2rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
`

export type WorkflowDisplayUnits = {
  lengthLabel: string
  areaLabel: string
  lengthStep: number
  areaStep: number
  lengthFromFeet: (feet: number) => number
  feetFromLength: (value: number) => number
  areaFromFeetSquared: (feetSquared: number) => number
  feetSquaredFromArea: (value: number) => number
  formatLength: (feet: number) => string
  formatArea: (feetSquared: number) => string
}

export const SHIFT_RESET_TOLERANCE_FEET = 1e-6

type WorkflowDisplayUnitConfig = {
  lengthUnit: GeckoMeasurementUnit
  lengthLabel: string
  areaLabel: string
  lengthStep: number
  areaStep: number
  lengthInputDecimals: number
  areaInputDecimals: number
  formattedDecimalPlaces: number
}

const WORKFLOW_DISPLAY_UNIT_CONFIG: Record<
  GeckoMeasurementSystem,
  WorkflowDisplayUnitConfig
> = {
  [GeckoMeasurementSystem.Imperial]: {
    lengthUnit: GeckoMeasurementUnit.Inches,
    lengthLabel: 'in',
    areaLabel: 'in^2',
    lengthStep: 0.1,
    areaStep: 0.25,
    lengthInputDecimals: 3,
    areaInputDecimals: 3,
    formattedDecimalPlaces: 2,
  },
  [GeckoMeasurementSystem.Metric]: {
    lengthUnit: GeckoMeasurementUnit.Millimeters,
    lengthLabel: 'mm',
    areaLabel: 'mm^2',
    lengthStep: 1,
    areaStep: 1000,
    lengthInputDecimals: 1,
    areaInputDecimals: 0,
    formattedDecimalPlaces: 0,
  },
}

export const PARAMETER_HELP_TEXT = {
  coreDfn:
    'Cells at or below this difference-from-nominal value seed automatic features. More negative values find fewer, more severe corrosion starts.',
  growDfn:
    'Cells at or below this value can be added to a feature when they touch a core cell. More negative values keep features tighter; less negative values let them grow.',
  minArea:
    'The smallest physical area an auto-detected matching zone must cover. Raise this to ignore small speckles and focus on zones worth matching.',
  merge:
    'How far apart detected groups can be and still be joined before features are created. Larger values combine nearby islands; smaller values keep them separate.',
  maxArea:
    'The largest physical area for an automatically detected matching zone. Large corrosion clusters are split into smaller local zones to avoid mixing different corrosion rates or damage patterns.',
  search:
    'How far around each target feature the matcher searches in the prior scan. Larger values tolerate more localization error but take longer and can find wrong repeated patterns.',
  distanceWeight:
    'How much the matcher prefers shorter shifts inside the search radius. Lower values let stronger pattern matches win farther from the target; higher values keep matches closer.',
  matchStep:
    'The spacing between candidate shifts during prior-scan matching, measured in scan bins. A value of 1 tests every adjacent cell shift.',
} as const

export function FieldBlock({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <FieldBlockField>
      <FieldTitle>{label}</FieldTitle>
      {children}
    </FieldBlockField>
  )
}

export function ParameterInput({
  label,
  help,
  value,
  step = 0.1,
  onChange,
}: {
  label: string
  help: string
  value: number
  step?: number
  onChange: (value: number) => void
}) {
  const [draftValue, setDraftValue] = useState(() => String(value))
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (isEditing) return
    setDraftValue(String(value))
  }, [isEditing, value])

  return (
    <ParameterField>
      <ParameterHeaderRow>
        <TruncatedFieldTitle>{label}</TruncatedFieldTitle>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpButton
              type='button'
              variant='ghost'
              size='icon-xs'
              aria-label={`About ${label}`}>
              <HelpIcon />
            </HelpButton>
          </TooltipTrigger>
          <HelpTooltipContent side='top' align='start' sideOffset={8}>
            <HelpTitle>{label}</HelpTitle>
            <HelpText>{help}</HelpText>
          </HelpTooltipContent>
        </Tooltip>
      </ParameterHeaderRow>
      <Input
        type='number'
        value={draftValue}
        step={step}
        inputMode='decimal'
        onFocus={() => setIsEditing(true)}
        onBlur={(event) => {
          const nextDraftValue = event.currentTarget.value
          setIsEditing(false)

          if (isIncompleteNumberInput(nextDraftValue)) {
            setDraftValue(String(value))
            return
          }

          const nextValue = Number(nextDraftValue)
          if (!Number.isFinite(nextValue)) {
            setDraftValue(String(value))
            return
          }

          onChange(nextValue)
          setDraftValue(String(nextValue))
        }}
        onChange={(event) => {
          const nextDraftValue = event.target.value
          setDraftValue(nextDraftValue)

          if (isIncompleteNumberInput(nextDraftValue)) return

          const nextValue = Number(nextDraftValue)
          if (Number.isFinite(nextValue)) {
            onChange(nextValue)
          }
        }}
      />
    </ParameterField>
  )
}

export function isIncompleteNumberInput(value: string): boolean {
  return ['', '+', '-', '.', '+.', '-.'].includes(value.trim())
}

export function formatCount(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`
}

export function EditableShiftMetric({
  label,
  valueFeet,
  displayUnits,
  onChange,
}: {
  label: string
  valueFeet: number
  displayUnits: WorkflowDisplayUnits
  onChange: (valueFeet: number) => void
}) {
  const [draftValue, setDraftValue] = useState(() =>
    String(displayUnits.lengthFromFeet(valueFeet))
  )
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (isEditing) return
    setDraftValue(String(displayUnits.lengthFromFeet(valueFeet)))
  }, [displayUnits, isEditing, valueFeet])

  return (
    <div>
      <ShiftMetricLabel>
        {label} ({displayUnits.lengthLabel})
      </ShiftMetricLabel>
      <ShiftMetricInput
        type='number'
        value={draftValue}
        step={displayUnits.lengthStep}
        inputMode='decimal'
        onFocus={() => setIsEditing(true)}
        onBlur={(event) => {
          const nextDraftValue = event.currentTarget.value
          setIsEditing(false)

          if (isIncompleteNumberInput(nextDraftValue)) {
            setDraftValue(String(displayUnits.lengthFromFeet(valueFeet)))
            return
          }

          const nextValue = Number(nextDraftValue)
          if (!Number.isFinite(nextValue)) {
            setDraftValue(String(displayUnits.lengthFromFeet(valueFeet)))
            return
          }

          onChange(displayUnits.feetFromLength(nextValue))
          setDraftValue(String(nextValue))
        }}
        onChange={(event) => {
          const nextDraftValue = event.target.value
          setDraftValue(nextDraftValue)

          if (isIncompleteNumberInput(nextDraftValue)) return

          const nextValue = Number(nextDraftValue)
          if (Number.isFinite(nextValue)) {
            onChange(displayUnits.feetFromLength(nextValue))
          }
        }}
      />
    </div>
  )
}

export function getWorkflowDisplayUnits(
  measurementSystem: GeckoMeasurementSystem
): WorkflowDisplayUnits {
  const config = WORKFLOW_DISPLAY_UNIT_CONFIG[measurementSystem]
  const lengthPerFoot = GeckoMeasurement.CreateFromValue(
    1,
    GeckoMeasurementUnit.Feet
  ).convertTo(config.lengthUnit).num
  const lengthFromFeet = (feet: number) =>
    roundDisplayValue(
      GeckoMeasurement.CreateFromValue(
        feet,
        GeckoMeasurementUnit.Feet
      ).convertTo(config.lengthUnit).num,
      config.lengthInputDecimals
    )
  const feetFromLength = (value: number) =>
    GeckoMeasurement.CreateFromValue(value, config.lengthUnit).convertTo(
      GeckoMeasurementUnit.Feet
    ).num
  const areaFromFeetSquared = (feetSquared: number) =>
    roundDisplayValue(
      feetSquared * lengthPerFoot * lengthPerFoot,
      config.areaInputDecimals
    )
  const feetSquaredFromArea = (value: number) =>
    value / (lengthPerFoot * lengthPerFoot)

  return {
    lengthLabel: config.lengthLabel,
    areaLabel: config.areaLabel,
    lengthStep: config.lengthStep,
    areaStep: config.areaStep,
    lengthFromFeet,
    feetFromLength,
    areaFromFeetSquared,
    feetSquaredFromArea,
    formatLength: (feet) =>
      `${lengthFromFeet(feet).toFixed(config.formattedDecimalPlaces)} ${config.lengthLabel}`,
    formatArea: (feetSquared) =>
      `${areaFromFeetSquared(feetSquared).toFixed(config.formattedDecimalPlaces)} ${config.areaLabel}`,
  }
}

export function roundDisplayValue(
  value: number,
  decimalPlaces: number
): number {
  return Number(value.toFixed(decimalPlaces))
}
